import { Link, useParams } from '@tanstack/react-router';
import { cn } from '@tempo-io/sui-class-utils';
import { CloudOff, GroupOff, Schedule, Warning } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActualCostCompletenessStatus } from '@/common/api/types/itemFinancials';

type NonCompleteStatus = Exclude<ActualCostCompletenessStatus, 'COMPLETE'>;

const STATUS_ICON: Record<NonCompleteStatus, { Icon: typeof Warning; colorClassName: string }> = {
  INCOMPLETE: { Icon: Warning, colorClassName: 'text-icon-warning' },
  MISSING_TEAM: { Icon: GroupOff, colorClassName: 'text-icon-warning' },
  PRESERVED: { Icon: Schedule, colorClassName: 'text-icon-info' },
  UNAVAILABLE: { Icon: CloudOff, colorClassName: 'text-icon-error' },
};

type StatusSource = 'unknown' | 'self' | 'descendants';

const getStatusSource = (
  status: NonCompleteStatus,
  selfStatus: ActualCostCompletenessStatus | null | undefined,
): StatusSource => {
  if (selfStatus == null) return 'unknown';
  return selfStatus === status ? 'self' : 'descendants';
};

const useStatusLabel = (status: NonCompleteStatus, source: StatusSource): string => {
  const { t } = useTranslation();
  switch (status) {
    case 'INCOMPLETE':
      if (source === 'self') return t(($) => $.financials.actualCostStatus.incompleteSelf);
      if (source === 'descendants')
        return t(($) => $.financials.actualCostStatus.incompleteDescendants);
      return t(($) => $.financials.actualCostStatus.incomplete);
    case 'MISSING_TEAM':
      if (source === 'descendants')
        return t(($) => $.financials.actualCostStatus.missingTeamDescendants);
      return t(($) => $.financials.actualCostStatus.missingTeam);
    case 'PRESERVED':
      return t(($) => $.financials.actualCostStatus.preserved);
    case 'UNAVAILABLE':
      if (source === 'self') return t(($) => $.financials.actualCostStatus.unavailableSelf);
      if (source === 'descendants')
        return t(($) => $.financials.actualCostStatus.unavailableDescendants);
      return t(($) => $.financials.actualCostStatus.unavailable);
  }
};

const ICON_CLASSNAME = 'size-3.5';
const INFO_TRIGGER_CLASSNAME = 'inline-flex shrink-0 cursor-help items-center justify-center';
const ACTION_TRIGGER_CLASSNAME =
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-border-warning bg-bg-warning-subtler p-0.5 hover:bg-bg-warning-subtler-hovered';

const IndicatorTooltip = ({ label, children }: { label: string; children: ReactNode }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-xs">{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const InfoIndicator = ({
  status,
  source,
  testId,
}: {
  status: NonCompleteStatus;
  source: StatusSource;
  testId: string;
}) => {
  const label = useStatusLabel(status, source);
  const { Icon, colorClassName } = STATUS_ICON[status];

  return (
    <IndicatorTooltip label={label}>
      <button
        type="button"
        aria-label={label}
        className={INFO_TRIGGER_CLASSNAME}
        data-testid={testId}
      >
        <Icon aria-hidden="true" className={cn(ICON_CLASSNAME, colorClassName)} />
      </button>
    </IndicatorTooltip>
  );
};

const AssignTeamLinkIndicator = ({ itemId, testId }: { itemId: number; testId: string }) => {
  const { t } = useTranslation();
  const { org, loopId } = useParams({ strict: false }) as { org?: string; loopId?: number };
  const label = t(($) => $.financials.actualCostStatus.missingTeamAssign);
  const { Icon, colorClassName } = STATUS_ICON.MISSING_TEAM;

  if (!org || !loopId) {
    return <InfoIndicator status="MISSING_TEAM" source="unknown" testId={testId} />;
  }

  return (
    <IndicatorTooltip label={label}>
      <Link
        to="/org/$org/loop/$loopId/portfolio/table"
        params={{ org, loopId }}
        search={{ itemId, jumpToItemId: itemId, panelTab: 'planScheduling' }}
        aria-label={label}
        className={ACTION_TRIGGER_CLASSNAME}
        data-testid={testId}
        onClick={(event) => event.stopPropagation()}
      >
        <Icon aria-hidden="true" className={cn(ICON_CLASSNAME, colorClassName)} />
      </Link>
    </IndicatorTooltip>
  );
};

export const ActualCostStatusIndicator = ({
  status,
  testId = 'actual-cost-status-indicator',
  itemId,
  selfStatus,
}: {
  status: ActualCostCompletenessStatus | null | undefined;
  testId?: string;
  itemId?: number;
  selfStatus?: ActualCostCompletenessStatus | null;
}) => {
  if (status == null || status === 'COMPLETE') return null;

  if (status === 'MISSING_TEAM' && selfStatus === 'MISSING_TEAM' && itemId != null) {
    return <AssignTeamLinkIndicator itemId={itemId} testId={testId} />;
  }

  return (
    <InfoIndicator status={status} source={getStatusSource(status, selfStatus)} testId={testId} />
  );
};
